import React from 'react';
import Icon from 'components/AppIcon';

const OrderHistoryTable = ({ orders, onSort, sortConfig, onReorder }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'filled': return 'text-success bg-success-50';
      case 'cancelled': return 'text-error bg-error-50';
      case 'partially_filled': return 'text-warning bg-warning-50';
      default: return 'text-text-secondary bg-secondary-50';
    }
  };

  const getOrderTypeColor = (type) => {
    switch (type) {
      case 'market': return 'text-accent bg-accent-50';
      case 'limit': return 'text-primary bg-primary-50';
      case 'stop-loss': return 'text-error bg-error-50';
      case 'stop-limit': return 'text-warning bg-warning-50';
      case 'trailing-stop': return 'text-secondary bg-secondary-100';
      default: return 'text-text-secondary bg-secondary-50';
    }
  };

  const getSideColor = (side) => {
    return side === 'buy' ? 'text-success' : 'text-error';
  };

  const SortButton = ({ column, children }) => (
    <button
      onClick={() => onSort(column)}
      className="flex items-center space-x-1 text-left font-body-medium text-sm text-text-secondary hover:text-text-primary transition-colors duration-trading-fast"
    >
      <span>{children}</span>
      <div className="flex flex-col">
        <Icon 
          name="ChevronUp" 
          size={12} 
          className={sortConfig.key === column && sortConfig.direction === 'asc' ? 'text-primary' : 'text-text-secondary'} 
        />
        <Icon 
          name="ChevronDown" 
          size={12} 
          className={sortConfig.key === column && sortConfig.direction === 'desc' ? 'text-primary' : 'text-text-secondary'} 
        />
      </div>
    </button>
  );

  if (orders.length === 0) {
    return null;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-secondary-50 border-b border-border">
          <tr>
            <th className="px-6 py-4 text-left">
              <SortButton column="timestamp">Date</SortButton>
            </th>
            <th className="px-6 py-4 text-left">
              <SortButton column="pair">Pair</SortButton>
            </th>
            <th className="px-6 py-4 text-left">
              <SortButton column="type">Type</SortButton>
            </th>
            <th className="px-6 py-4 text-left">
              <SortButton column="side">Side</SortButton>
            </th>
            <th className="px-6 py-4 text-left">
              <SortButton column="quantity">Quantity</SortButton>
            </th>
            <th className="px-6 py-4 text-left">
              <SortButton column="price">Price</SortButton>
            </th>
            <th className="px-6 py-4 text-left">
              <SortButton column="filled">Filled</SortButton>
            </th>
            <th className="px-6 py-4 text-left">
              <SortButton column="total">Total</SortButton>
            </th>
            <th className="px-6 py-4 text-left">
              <SortButton column="fees">Fees</SortButton>
            </th>
            <th className="px-6 py-4 text-left">
              <SortButton column="status">Status</SortButton>
            </th>
            <th className="px-6 py-4 text-left">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {orders.map((order) => (
            <tr key={order.id} className="hover:bg-secondary-50 transition-colors duration-trading-fast">
              <td className="px-6 py-4">
                <div className="text-sm">
                  <p className="font-data text-text-primary">
                    {order.timestamp.toLocaleDateString()}
                  </p>
                  <p className="text-text-secondary">
                    {order.timestamp.toLocaleTimeString()}
                  </p>
                  {order.executionTime && (
                    <p className="text-text-secondary text-xs">
                      Executed: {order.executionTime.toLocaleTimeString()}
                    </p>
                  )}
                </div>
              </td>
              <td className="px-6 py-4">
                <span className="font-data font-body-medium text-sm text-text-primary">
                  {order.pair}
                </span>
              </td>
              <td className="px-6 py-4">
                <span className={`px-2 py-1 rounded-full text-xs font-caption-medium ${getOrderTypeColor(order.type)}`}>
                  {order.type.replace('-', ' ').toUpperCase()}
                </span>
              </td>
              <td className="px-6 py-4">
                <span className={`font-body-medium text-sm ${getSideColor(order.side)}`}>
                  {order.side.toUpperCase()}
                </span>
              </td>
              <td className="px-6 py-4">
                <span className="font-data text-sm text-text-primary">
                  {order.quantity.toLocaleString()}
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm">
                  <p className="font-data text-text-primary">
                    ${order.price.toLocaleString()}
                  </p>
                  {order.avgPrice && order.avgPrice !== order.price && (
                    <p className="text-text-secondary text-xs">
                      Avg: ${order.avgPrice.toLocaleString()}
                    </p>
                  )}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm">
                  <p className="font-data text-text-primary">
                    {order.filled.toLocaleString()}
                  </p>
                  <p className="text-text-secondary text-xs">
                    {((order.filled / order.quantity) * 100).toFixed(1)}%
                  </p>
                </div>
              </td>
              <td className="px-6 py-4">
                <span className="font-data text-sm text-text-primary">
                  ${order.total.toLocaleString()}
                </span>
              </td>
              <td className="px-6 py-4">
                <span className="font-data text-sm text-text-primary">
                  ${order.fees.toFixed(2)}
                </span>
              </td>
              <td className="px-6 py-4">
                <span className={`px-2 py-1 rounded-full text-xs font-caption-medium ${getStatusColor(order.status)}`}>
                  {order.status.replace('_', ' ').toUpperCase()}
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onReorder(order)}
                    className="p-1 text-text-secondary hover:text-accent transition-colors duration-trading-fast"
                    title="Reorder"
                  >
                    <Icon name="Copy" size={16} />
                  </button>
                  <button
                    onClick={() => console.log('View details:', order.id)}
                    className="p-1 text-text-secondary hover:text-primary transition-colors duration-trading-fast"
                    title="View Details"
                  >
                    <Icon name="Eye" size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default OrderHistoryTable;